import { useMemo } from 'react';
import {
  MANAGEMENT_PERMISSIONS_SERVER,
  WORKSPACE_OWNER_VALUE,
  WORKSPACE_MEMBER_VALUE
} from '@shared/constants/permissions';
import {
  formatPermissionValueToString,
  getPermissionByType,
  getPermissionValueByType,
  getUserUniqueName,
  isUserPermissionValid,
  updatePermissionByType
} from '@shared/utils/permissions';

import {
  CANNOT_CHANGE_MY_ROLE_IN_WS_TOOLTIP,
  CANNOT_CHANGE_ORG_ADMIN_ROLE_IN_WS_TOOLTIP,
  WORKSPACE_MEMBER_PERMISSIONS
} from '@shared/components/ManageUsersModalTable/constants';
import {
  Permission,
  User
} from '@shared/components/ManageUsersModalTable/types';

export const useManageUsersRolePopover = (
  permissions: Permission[],
  currentUser: User,
  username: string,
  ifChangeWsRoleDisabled: boolean,
  ifUserAdmin: boolean,
  ifUserOpikMember: boolean,
  setPermissions: (permissions: Permission[]) => void
) => {
  const currentUserUserName = getUserUniqueName(currentUser);
  const wsManagementPermissionValue = getPermissionValueByType(
    permissions,
    MANAGEMENT_PERMISSIONS_SERVER.MANAGEMENT
  );
  const ifChangeMadeForCurrentUser = currentUserUserName === username;
  const isDisabled = ifChangeWsRoleDisabled || ifUserAdmin;

  return useMemo(() => {
    const checkboxesOptions = WORKSPACE_MEMBER_PERMISSIONS.map(perm => ({
      ...perm,
      value: perm.key,
      checked: isUserPermissionValid(
        getPermissionByType(permissions, perm.key)
      ),
      disabled:
        ifUserOpikMember &&
        [
          MANAGEMENT_PERMISSIONS_SERVER.PROJECT_VISIBILITY,
          MANAGEMENT_PERMISSIONS_SERVER.MODEL_REGISTRY
        ].includes(perm.key),
      color: 'primary' as const,
      controlType: 'checkbox' as const
    }));

    return {
      controlType: 'radio' as const,
      controlValue: wsManagementPermissionValue,
      onChange: (ev: React.ChangeEvent<HTMLInputElement>) =>
        setPermissions(
          updatePermissionByType(
            permissions,
            MANAGEMENT_PERMISSIONS_SERVER.MANAGEMENT,
            ev?.target?.value
          )
        ),
      options: [
        {
          key: `${MANAGEMENT_PERMISSIONS_SERVER.MANAGEMENT}-owner`,
          value: WORKSPACE_OWNER_VALUE,
          label: 'Workspace Owner',
          text: 'Full permissions to all workspace resources',
          controlType: 'radio' as const
        },
        {
          key: `${MANAGEMENT_PERMISSIONS_SERVER.MANAGEMENT}-member`,
          value: WORKSPACE_MEMBER_VALUE,
          label: 'Workspace Member',
          text: 'Limited permissions. You can give customized ones',
          controlType: 'radio' as const,
          disabled: isDisabled,
          tooltip: isDisabled
            ? {
                arrow: true,
                title: ifChangeMadeForCurrentUser
                  ? CANNOT_CHANGE_MY_ROLE_IN_WS_TOOLTIP
                  : CANNOT_CHANGE_ORG_ADMIN_ROLE_IN_WS_TOOLTIP,
                placement: 'left' as const
              }
            : null,
          list:
            wsManagementPermissionValue === WORKSPACE_MEMBER_VALUE
              ? {
                  options: checkboxesOptions,
                  controlType: 'checkbox' as const,
                  onChange: (
                    ev: React.ChangeEvent<HTMLInputElement>,
                    { value }: { value: string }
                  ) => {
                    setPermissions(
                      updatePermissionByType(
                        permissions,
                        value,
                        formatPermissionValueToString(ev?.target?.checked)
                      )
                    );
                  }
                }
              : null
        }
      ]
    };
  }, [
    permissions,
    wsManagementPermissionValue,
    ifChangeMadeForCurrentUser,
    setPermissions,
    isDisabled
  ]);
};
